import { Injectable } from '@angular/core';
import * as Sentry from '@sentry/angular';
import { environment } from '@environments/environment';

@Injectable({
  providedIn: 'root'
})
export class SentryService {
  private sentryConfig = (environment as any).sentry;
  private isSentryEnabled = this.sentryConfig?.enabled || false;

  /**
   * Identifica al usuario en Sentry después de obtener su información.
   *
   * Este método puede ser llamado múltiples veces durante el flujo de onboarding:
   * - Después de ingresar el email
   * - Después de obtener el perfil completo
   * - En cualquier punto donde se tenga nueva información del usuario
   *
   * @param userId ID único del cliente (requerido)
   * @param email Correo electrónico del usuario (opcional)
   * @param additionalData Información adicional del usuario (opcional)
   */
  setUser(userId: string, email?: string, additionalData?: any): void {
    if (!this.isSentryEnabled || !userId) return;

    const userData: Sentry.User = {
      id: userId,
      ...(this.sentryConfig.sendDefaultPii && email && { email }),
      ...(this.sentryConfig.sendDefaultPii && additionalData)
    };

    Sentry.setUser(userData);
  }

  /**
   * Establece información parcial del usuario cuando aún no tenemos el ID completo.
   * Útil para trackear errores en etapas tempranas del onboarding.
   *
   * @param email Email del usuario
   */
  setUserByEmail(email: string): void {
    if (!this.isSentryEnabled || !email) return;

    if (this.sentryConfig.sendDefaultPii) {
      Sentry.setUser({ email, id: `temp-${email}` }); // ID temporal hasta obtener el real
    } else {
      Sentry.setUser({ id: 'anonymous-user' });
    }
  }

  /**
   * Agrega o actualiza información del usuario sin sobreescribir lo anterior.
   * Útil para ir agregando datos progresivamente durante el onboarding.
   *
   * @param additionalData Nueva información a agregar
   */
  updateUser(additionalData: any): void {
    if (!this.isSentryEnabled) return;

    const currentUser = Sentry.getCurrentScope().getUser();
    if (!currentUser) return;

    if (this.sentryConfig.sendDefaultPii) {
      Sentry.setUser({ ...currentUser, ...additionalData });
    } else {
      Sentry.setUser({ id: currentUser.id });
    }
  }

  clearUser(): void {
    if (!this.isSentryEnabled) return;
    Sentry.setUser(null);
  }

  /**
   * Agrega contexto adicional que se adjuntará a todos los eventos.
   * Útil para información de sesión, configuración, etc.
   *
   * @param key Nombre del contexto
   * @param data Datos del contexto
   */
  setContext(key: string, data: any): void {
    if (!this.isSentryEnabled) return;
    Sentry.setContext(key, data);
  }

  /**
   * Agrega tags que facilitan filtrar eventos en Sentry.
   *
   * @param key Nombre del tag
   * @param value Valor del tag
   */
  setTag(key: string, value: string): void {
    if (!this.isSentryEnabled) return;
    Sentry.setTag(key, value);
  }

  /**
   * Captura un error manualmente con contexto adicional.
   * Útil para errores que se capturan con try-catch.
   *
   * @param error Error a capturar
   * @param context Contexto adicional del error
   */
  captureException(error: any, context?: any): void {
    if (!this.isSentryEnabled) return;

    if (context) {
      Sentry.withScope((scope) => {
        Object.keys(context).forEach(key => scope.setExtra(key, context[key]));
        Sentry.captureException(error);
      });
    } else {
      Sentry.captureException(error);
    }
  }

  /**
   * Captura un mensaje informativo o de advertencia.
   *
   * @param message Mensaje a capturar
   * @param level Nivel de severidad ('info', 'warning', 'error')
   */
  captureMessage(message: string, level: 'info' | 'warning' | 'error' = 'info'): void {
    if (!this.isSentryEnabled) return;
    Sentry.captureMessage(message, level);
  }

  isEnabled(): boolean {
    return this.isSentryEnabled;
  }

  isPiiEnabled(): boolean {
    return this.isSentryEnabled && this.sentryConfig.sendDefaultPii;
  }
}
